import React, { useState, useEffect, useContext } from "react";
import config from "../../config/config";
import { apiCall } from "../../_services/apiCall";
import { ROLE_ID_MAPPING } from "../../_constants/constants";
import { ProfilePicContext } from "../../contextApi/profileContext";
import { authenticationService } from "../../_services/authentication";
import { Modal, ModalHeader } from "../../globalComponents/ui/Modal";
import { PrimaryButton, SecondaryButton } from "../../globalComponents/ui/Button";
import Loader from "../loader/Loader";
import styled from "styled-components";
import { usePermission } from "../../hooks/usePermission";
import {
  SettingLayout,
  SettingsCard,
  SettingsModalBody, SettingsModalFooter,
  SettingsFormGroup, SettingsFormLabel, SettingsFormInput,
} from "./SettingShell";

const ProfilePics = "https://res.cloudinary.com/dmklsntsw/image/upload/v1658480882/dummyPic.75a04487_fqfqey.png";

const AudienceOptions = [
  { label: "Permanent Employee", value: "EMPLOYEES_PERMANENT" },
  { label: "Other than Permanent Employee", value: "EMPLOYEES_TEMPORARY" },
  { label: "Permanent Worker", value: "WORKERS_PERMANENT" },
  { label: "Other than Permanent Worker", value: "WORKERS_TEMPORARY" },
  { label: "KMP", value: "KMP" },
  { label: "BOD", value: "BOD" },
  { label: "Customer", value: "CUSTOMERS" },
  { label: "Supplier", value: "SUPPLIERS" },
  { label: "Distributor", value: "DISTRIBUTORS" },
];

// ─── Card inner wrapper ───────────────────────────────────────────────────────
// This is the key fix: SettingsCard likely has no padding (or very little).
// Wrapping everything in CardInner gives a consistent 24px breathing room
// on all 4 sides so inputs never touch the card border.

const CardInner = styled.div`
  padding: 24px 28px 28px;
`;

// ─── Avatar ───────────────────────────────────────────────────────────────────

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 28px;
  padding-bottom: 22px;
  border-bottom: 1px solid #f1f5f9;
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #fff;
  box-shadow: 0 2px 10px rgba(0,0,0,0.10);
  background: #f8fafc;

  img { width: 100%; height: 100%; object-fit: cover; display: block; }

  input[type="file"] {
    position: absolute; inset: 0; opacity: 0; cursor: pointer; z-index: 2;
  }

  .hover-overlay {
    position: absolute; inset: 0;
    background: rgba(0,0,0,0.38);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 13px;
    opacity: 0; transition: opacity 0.18s;
    pointer-events: none; z-index: 1;
  }
  &:hover .hover-overlay { opacity: 1; }
`;

const UserBasicInfo = styled.div`
  display: flex; flex-direction: column; gap: 3px; min-width: 0;
`;

const NameInput = styled.input`
  border: none; background: transparent;
  font-size: 18px; font-weight: 700; color: #0f172a;
  padding: 0; width: 100%;
  &:focus { outline: none; border-bottom: 1.5px solid #3f88a5; }
  &[readonly] { cursor: default; }
`;

const RoleText = styled.div`font-size: 13px; color: #475569; font-weight: 500;`;
const EmailText = styled.div`font-size: 12px; color: #94a3b8;`;

// ─── Section label ────────────────────────────────────────────────────────────

const SectionLabel = styled.div`
  display: flex; align-items: center; gap: 7px;
  margin-bottom: 18px;
  font-size: 10px; font-weight: 700;
  color: #3f88a5; text-transform: uppercase; letter-spacing: 0.6px;
`;

// ─── Form grid ────────────────────────────────────────────────────────────────
// All grids are fully box-constrained so they never overflow the card.

const FormGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 24px;
  width: 100%;
  box-sizing: border-box;
  @media (max-width: 580px) { grid-template-columns: 1fr; }
`;

const FormGrid1 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  width: 100%;
  box-sizing: border-box;
`;

const FieldGroup = styled.div`
  display: flex; flex-direction: column; gap: 5px; min-width: 0;
`;

const FieldLabel = styled.label`
  font-size: 10px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: 0.5px; display: block;
`;

const FieldInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  font-size: 13px;
  color: ${({ readOnly }) => (readOnly ? "#475569" : "#1e293b")};
  background: ${({ readOnly }) => (readOnly ? "#f8fafc" : "#fff")};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus:not([readonly]) {
    border-color: #3f88a5;
    box-shadow: 0 0 0 3px rgba(63,136,165,0.08);
  }
  &[readonly] { cursor: default; }
  &::placeholder { color: #cbd5e1; }
`;

const Divider = styled.div`
  height: 1px; background: #f1f5f9; margin: 20px 0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function Profile() {
  const context = useContext(ProfilePicContext);
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [register_company_name, setRegisterCompanyName] = useState("");
  const [logo, setLogo] = useState(ProfilePics);
  const [position, setPosition] = useState("");
  const [category, setCategory] = useState("");
  const [department, setDepartment] = useState("");
  const [businessUnit, setBusinessUnit] = useState("");
  const [division, setDivision] = useState("");
  const [company_industry, setCompanyIndustry] = useState("");
  const [showChangePasswordPopup, setShowChangePasswordPopup] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);

  // New capability check (falls back to legacy code automatically)
  const [canUpdateName] = usePermission("profile-management.update-name",  "UPDATE_NAME");
  const [canChangePass] = usePermission("profile-management.change-password", "CHANGE_PASSWORD");

  useEffect(() => {
    const fetchData = async () => {
      const userId = Number(localStorage.getItem("user_temp_id"));
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getProfileData?userId=${userId}&role_id=${ROLE_ID_MAPPING[localStorage.getItem("role")]}`
      );
      if (isSuccess && data?.user) {
        setFirstName(data.user.first_name);
        setLastName(data.user.last_name);
        setEmail(data.user.email);
        setBusinessNumber(data.user.business_number);
        setCategory(data.user.categoryId);
        setDepartment(data.user.departmentId);
        setBusinessUnit(data.user.businessUnit);
        setDivision(data.user.division);
        setRegisterCompanyName(data.user.register_company_name);
        setCompanyIndustry(data.user.company_industry);
        setLogo(data.user.profile_picture || ProfilePics);
        setPosition(data.user.position);
      }
      setIsLoaded(true);
    };
    fetchData();
  }, []);

  const updateProfileForUser = async (updatedData) => {
    await apiCall(config.POSTLOGIN_API_URL_COMPANY + "updateUserProfile", {}, { data: updatedData }, "POST");
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    const parts = val.split(" ");
    const f = parts[0];
    const l = parts.slice(1).join(" ");
    setFirstName(f);
    setLastName(l);
    updateProfileForUser({ firstName: f, lastName: l });
  };

  const logout = () => {
    authenticationService.logout();
    localStorage.clear();
    window.location.href = "/#/";
    window.location.reload();
  };

  const handlePasswordChange = async () => {
    if (password !== confirmPassword) return;
    setLoading(true);
    const { isSuccess } = await apiCall(
      config.POSTLOGIN_API_URL_COMPANY + "changePassword", {}, { password }, "POST"
    );
    setLoading(false);
    if (isSuccess) logout();
  };


  if (!isLoaded) return <div style={{ padding: 60 }}><Loader /></div>;

  return (
    <SettingLayout
      title="Profile Management"
      subtitle="Manage your personal and company information"
      rightContent={
        canChangePass && (
          <SecondaryButton onClick={() => setShowChangePasswordPopup(true)}>
            <i className="fas fa-key" style={{ marginRight: 8 }} />
            Change Password
          </SecondaryButton>
        )
      }
    >
      {/* padding: 0 on the card itself; CardInner owns all inner spacing */}
      <SettingsCard style={{ padding: 0 }}>
        <CardInner>

          {/* ── Avatar + name ──────────────────────────────────────────── */}
          <ProfileHeader>
            <AvatarContainer>
              <img src={logo} alt="Profile" />
              <div className="hover-overlay">
                <i className="fas fa-camera" />
              </div>
              <input type="file" onChange={() => { }} />
            </AvatarContainer>

            <UserBasicInfo>
              <NameInput
                value={`${firstName} ${lastName}`}
                onChange={canUpdateName ? handleNameChange : undefined}
                readOnly={!canUpdateName}
              />
              <RoleText>{position || "No position defined"}</RoleText>
              <EmailText>{email}</EmailText>
            </UserBasicInfo>
          </ProfileHeader>

          {/* ── Company Details ─────────────────────────────────────────── */}
          <SectionLabel>
            <i className="fas fa-info-circle" style={{ fontSize: 11 }} />
            Company Details
          </SectionLabel>

          {/* Row 1: Company Name + Business Number */}
          <FormGrid2 style={{ marginBottom: 16 }}>
            <FieldGroup>
              <FieldLabel>Company Name</FieldLabel>
              <FieldInput value={register_company_name} readOnly />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Business Number</FieldLabel>
              <FieldInput value={businessNumber} readOnly />
            </FieldGroup>
          </FormGrid2>

          {/* Row 2: Industry — full width */}
          <FormGrid1 style={{ marginBottom: 0 }}>
            <FieldGroup>
              <FieldLabel>Industry</FieldLabel>
              <FieldInput value={company_industry} readOnly />
            </FieldGroup>
          </FormGrid1>

          <Divider />

          {/* Row 3: Category + Department */}
          <FormGrid2 style={{ marginBottom: 16 }}>
            <FieldGroup>
              <FieldLabel>Category</FieldLabel>
              <FieldInput
                value={AudienceOptions.find(o => o.value === category)?.label || category}
                readOnly
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Department</FieldLabel>
              <FieldInput value={department} readOnly />
            </FieldGroup>
          </FormGrid2>

          {/* Row 4: Business Unit + Division */}
          <FormGrid2>
            <FieldGroup>
              <FieldLabel>Business Unit</FieldLabel>
              <FieldInput value={businessUnit} readOnly />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Division</FieldLabel>
              <FieldInput value={division} readOnly />
            </FieldGroup>
          </FormGrid2>

        </CardInner>
      </SettingsCard>

      {/* ── Change Password Modal ────────────────────────────────────────── */}
      <Modal
        show={showChangePasswordPopup}
        onClose={() => setShowChangePasswordPopup(false)}
        maxWidth={400}
      >
        <ModalHeader
          icon="fas fa-lock"
          title="Change Password"
          subtitle="Your session will end after the update"
          onClose={() => setShowChangePasswordPopup(false)}
        />
        <SettingsModalBody>
          <SettingsFormGroup>
            <SettingsFormLabel>New Password</SettingsFormLabel>
            <SettingsFormInput
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </SettingsFormGroup>
          <SettingsFormGroup>
            <SettingsFormLabel>Confirm Password</SettingsFormLabel>
            <SettingsFormInput
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />
            {password && confirmPassword && password !== confirmPassword && (
              <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>
                Passwords do not match
              </div>
            )}
          </SettingsFormGroup>
        </SettingsModalBody>
        <SettingsModalFooter>
          <SecondaryButton onClick={() => setShowChangePasswordPopup(false)}>Cancel</SecondaryButton>
          <PrimaryButton
            onClick={handlePasswordChange}
            disabled={!password || password !== confirmPassword || loading}
          >
            {loading ? "Updating…" : "Update Password"}
          </PrimaryButton>
        </SettingsModalFooter>
      </Modal>
    </SettingLayout>
  );
}